import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Building2, CalendarDays, Clock3, Download, LockKeyhole, MonitorSmartphone, ShieldCheck } from 'lucide-react';
import { accountAPI, authAPI, tenantAPI } from '../../../../api/client';
import { useAuth } from '../../../../context/AuthContext';
import { useTenant } from '../../../../context/TenantContext';
import { DATE_FORMATS, TIME_FORMATS } from '../../../../lib/displayFormat';
import { apiError } from '../../../../lib/host';
import { useT } from '../../../../lib/i18n';
import { ErrorBanner, useBasic } from '../../ui';
import AvatarPicker from '../components/AvatarPicker';
import { Card, Field, SectionHead } from '../components/SettingsUI';
import TimezoneSelect from '../components/TimezoneSelect';

const INDUSTRIES = ['Real Estate', 'Healthcare', 'Education', 'Finance & Insurance', 'Retail & E-commerce', 'Hospitality & Travel',
  'Automotive', 'Professional Services', 'IT & Software', 'Manufacturing', 'Logistics', 'Other'];

/** Download a ZIP the server sent. */
export function saveZip(res) {
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `telecalx-my-data-${new Date().toISOString().slice(0, 10)}.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Settings › Profile: the person's own details and photo, the company's information (owners and admins edit it). */
export default function ProfileSection() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const { tenant, can, reloadTenant } = useTenant();
  const { notify, roleLabel } = useBasic();
  const canEdit = can('settings.write');
  const [me, setMe] = useState({ name: user.name, phone: user.phone || '' });
  const [company, setCompany] = useState({
    name: tenant.name, website: tenant.website || '', industry: tenant.industry || '', timezone: tenant.timezone || 'Asia/Kolkata',
    date_format: tenant.date_format || 'DD MMM YYYY', time_format: tenant.time_format || '12',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const companyChanges = Object.fromEntries(Object.entries(company).filter(([k, v]) => {
    const was = { ...tenant, timezone: tenant.timezone || 'Asia/Kolkata', date_format: tenant.date_format || 'DD MMM YYYY', time_format: tenant.time_format || '12' }[k] || '';
    return String(v).trim() !== String(was);
  }));
  const meChanges = Object.fromEntries(Object.entries(me).filter(([k, v]) => v.trim() !== (user[k] || '')));
  const dirty = Object.keys(meChanges).length > 0 || (canEdit && Object.keys(companyChanges).length > 0);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      if (Object.keys(meChanges).length) {
        if (!me.name.trim()) throw new Error(t('Enter your full name'));
        await authAPI.updateMe(Object.fromEntries(Object.entries(meChanges).map(([k, v]) => [k, v.trim()])));
        await refresh();
      }
      if (canEdit && Object.keys(companyChanges).length) {
        const res = await tenantAPI.update(companyChanges);
        await reloadTenant();
        setCompany((c) => ({ ...c, website: res.data.website || '' }));
      }
      notify(t('Your profile has been saved.'));
    } catch (err) {
      setError(err.response ? apiError(err) : err.message);
    } finally {
      setBusy(false);
    }
  };

  const exportData = async () => {
    try {
      saveZip(await accountAPI.exportData());
      notify(t('Your data is downloading.'));
    } catch (err) {
      setError(apiError(err, t('Could not prepare your data. Please try again.')));
    }
  };

  const setC = (k) => (e) => setCompany({ ...company, [k]: e?.target ? e.target.value : e });
  const actions = [
    [LockKeyhole, 'purple', t('Change Password'), t('Update your account password'), () => navigate('/settings/security?focus=password')],
    [ShieldCheck, 'green', t('Two-Factor Authentication'), t('Add an extra layer of security'), () => navigate('/settings/security?focus=2fa')],
    [MonitorSmartphone, 'blue', t('Active Sessions'), t('Manage your logged-in devices'), () => navigate('/settings/security?focus=sessions')],
    [Download, 'orange', t('Download My Data'), t('Export your account data'), exportData],
  ];

  return (
    <>
      <SectionHead title={t('Profile')} subtitle={t('Manage your personal information and company details.')}>
        <button className="primary-action" disabled={busy || !dirty} onClick={save}>{busy ? t('Saving…') : t('Save Changes')}</button>
      </SectionHead>
      <ErrorBanner message={error} />

      <Card title={t('Account Information')} subtitle={t('Your personal account details.')}>
        <div className="st-account">
          <AvatarPicker name={user.name} onError={setError} onSaved={notify} />
          <div className="st-grid">
            <Field label={t('Full Name')}><input value={me.name} maxLength={255} onChange={(e) => setMe({ ...me, name: e.target.value })} /></Field>
            <Field label={t('Email Address')}><input value={user.email} disabled /></Field>
            <Field label={t('Phone Number')} hint={t('TeleCalX rings this number when you make a call.')}>
              <input value={me.phone} maxLength={20} inputMode="tel" onChange={(e) => setMe({ ...me, phone: e.target.value })} />
            </Field>
            <Field label={t('Role')}><input value={t(roleLabel)} disabled /></Field>
          </div>
        </div>
      </Card>

      <Card title={t('Company Information')} subtitle={canEdit ? t('Your company and workspace details.') : t('Only owners and admins can change these.')}>
        <div className="st-grid">
          <Field label={t('Company Name')}><input value={company.name} minLength={2} maxLength={255} disabled={!canEdit} onChange={setC('name')} /></Field>
          <Field label={t('Website')}><input value={company.website} placeholder="https://www.example.com" maxLength={255} disabled={!canEdit} onChange={setC('website')} /></Field>
          <Field label={t('Industry')} icon={Building2}>
            <select value={company.industry} disabled={!canEdit} onChange={setC('industry')}>
              <option value="">{t('Choose your industry')}</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{t(i)}</option>)}
            </select>
          </Field>
          <Field label={t('Timezone')} icon={Clock3}><TimezoneSelect value={company.timezone} disabled={!canEdit} onChange={setC('timezone')} /></Field>
          <Field label={t('Date Format')} icon={CalendarDays}>
            <select value={company.date_format} disabled={!canEdit} onChange={setC('date_format')}>{DATE_FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </Field>
          <Field label={t('Time Format')} icon={Clock3}>
            <select value={company.time_format} disabled={!canEdit} onChange={setC('time_format')}>{TIME_FORMATS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}</select>
          </Field>
        </div>
      </Card>

      <Card title={t('Profile Actions')} subtitle={t('Manage your account settings and data.')}>
        <div className="st-action-cards">
          {actions.map(([Icon, tone, title, sub, run]) => (
            <button type="button" key={title} className="st-action-card" onClick={run}>
              <span className={`st-icon ${tone}`}><Icon size={18} /></span>
              <span><b>{title}</b><small>{sub}</small></span>
              <ArrowRight size={15} />
            </button>
          ))}
        </div>
      </Card>
    </>
  );
}
